import { Controller } from "@hotwired/stimulus"
import { wizardMobile } from "../modules/wizard_layout.js"

// Campo tipo select del wizard (sección del menú): un botón con el valor elegido y un <dialog>
// con las opciones como lista de radios. En el nivel mobile del wizard abre modal como bottom
// sheet; en compact y wide abre anclado al campo como popover (show(), cierre por clic afuera y
// Escape). El valor vive en el hidden del form. Doc: _docs/features/products/partner_product_form.md

export default class extends Controller {
  static targets = ["input", "button", "label", "dialog", "option"]
  static values = { placeholder: { type: String, default: "Elige una opción" } }

  connect() {
    this.onDocumentClick = event => { if (!this.element.contains(event.target)) this.close() }
    this.onKeydown = event => { if (event.key === "Escape") this.close() }
    this.beforeCache = () => this.close()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    this.#paint()
  }

  disconnect() {
    this.close()
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  toggle() {
    this.dialogTarget.open ? this.close() : this.open()
  }

  open() {
    const dialog = this.dialogTarget
    if (dialog.open) return
    this.modal = wizardMobile(this.element)
    this.element.classList.toggle("is-modal", this.modal)
    this.modal ? dialog.showModal() : dialog.show()
    this.buttonTarget.setAttribute("aria-expanded", "true")
    if (!this.modal) {
      document.addEventListener("click", this.onDocumentClick, true)
      document.addEventListener("keydown", this.onKeydown)
    }
    const selected = this.optionTargets.find(option => option.getAttribute("aria-selected") === "true")
    ;(selected || this.optionTargets[0])?.focus()
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  // close del <dialog> (Escape nativo, pick, close()): deja el campo como estaba
  closed() {
    document.removeEventListener("click", this.onDocumentClick, true)
    document.removeEventListener("keydown", this.onKeydown)
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.element.classList.remove("is-modal")
  }

  // El clic fuera del panel llega al propio <dialog> (su ::backdrop)
  backdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  pick(event) {
    const option = event.currentTarget
    this.inputTarget.value = option.dataset.value
    this.#paint()
    this.close()
    this.buttonTarget.focus()
    this.dispatch("change", { detail: { value: option.dataset.value, label: option.dataset.label } })
  }

  // Borrador local: el valor elegido; al restaurar, solo si la opción todavía existe (una sección borrada se omite)
  get draft() {
    return this.inputTarget.value
  }

  restoreDraft(value) {
    const option = this.optionTargets.find(item => item.dataset.value === String(value))
    if (!option) return { skipped: 1 }
    this.inputTarget.value = option.dataset.value
    this.#paint()
    this.dispatch("change", { detail: { value: option.dataset.value, label: option.dataset.label } })
    return { skipped: 0 }
  }

  #paint() {
    const value = this.inputTarget.value
    const selected = this.optionTargets.find(option => option.dataset.value === value)
    this.optionTargets.forEach(option => option.setAttribute("aria-selected", option === selected))
    this.labelTarget.textContent = selected ? selected.dataset.label : this.placeholderValue
    this.element.classList.toggle("has-value", Boolean(selected))
  }
}
